import type {
  ContributionAction,
  ContributionState,
  SourceScope,
  TeacherNote,
  TeacherRunContextInput,
  TeacherRunForwardedProps,
} from "./types";

export function getSourceScopeLabel(scope: SourceScope): string {
  const labels: Record<SourceScope, string> = {
    private_note: "My Notes",
    approved_knowledge: "Approved Knowledge",
    external_web: "External/Web",
    conversation_attachment: "Attachments",
    pending_contribution: "Pending Contribution",
  };

  return labels[scope];
}

export function filterNotesForContext(
  notes: readonly TeacherNote[],
  context: { subjectAgentId: string; course?: string },
): TeacherNote[] {
  // Notes scoped to a specific agent OR to "default" (treated as the
  // teacher's shared workspace, surfaced across any agent picker
  // choice) are visible. This matters since the AgentPicker can land
  // on agents the seed notes weren't authored against — strict
  // equality would leave the notes panel empty for most picks.
  return notes.filter((note) => {
    const agentMatch =
      note.subjectAgentId === context.subjectAgentId ||
      note.subjectAgentId === "default";
    if (!agentMatch) return false;
    if (context.course && note.course && note.course !== context.course)
      return false;
    return true;
  });
}

export function buildTeacherRunContext(
  input: TeacherRunContextInput,
): TeacherRunForwardedProps {
  return {
    school_subject_agent_id: input.subjectAgentId,
    course_assistance_id: input.courseAssistanceId,
    selected_private_note_ids: input.selectedNoteIds,
    grade_band: input.gradeBand,
    course: input.course,
    standard: input.standard,
    school_class_id: input.schoolClassId ?? null,
    school_class_name: input.schoolClassName ?? null,
    school_subject: input.schoolSubject ?? null,
  };
}

export function transitionContribution(
  state: ContributionState,
  action: ContributionAction,
): ContributionState {
  if (
    action === "submit" &&
    (state.status === "private" || state.status === "rejected")
  ) {
    return { status: "pending" };
  }
  if (action === "withdraw" && state.status === "pending") {
    return { status: "private" };
  }
  if (action === "approve" && state.status === "pending") {
    return { status: "approved" };
  }
  if (action === "reject" && state.status === "pending") {
    return {
      status: "rejected",
      reviewerNote: "Needs source cleanup before approval.",
    };
  }

  return state;
}
